import type z from 'zod/v4'
import { boolean, index, integer, pgTable, primaryKey, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { BookCollection } from './book-collection/book-collection'
import { Book } from './book/book'
import { LessonModule } from './lesson/lesson-module/lesson-module'

export const ContentSection = pgTable(
  'content_section',
  {
    id: uuid().notNull().primaryKey(),
    title: text().notNull(),
    description: text(),
    isActive: boolean().notNull().default(true),
    order: integer().notNull().default(0),
    updatedAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
  },
  (columns) => [
    index('content_section_is_active_idx').on(columns.isActive),
    index('content_section_order_idx').on(columns.order),
    index('content_section_updated_at_idx').on(columns.updatedAt),
    index('content_section_created_at_idx').on(columns.createdAt),
  ],
)

export const ContentSectionToBook = pgTable(
  'content_section_to_book',
  {
    contentSectionId: uuid()
      .notNull()
      .references(() => ContentSection.id, { onDelete: 'cascade' }),
    bookId: uuid()
      .notNull()
      .references(() => Book.id, { onDelete: 'cascade' }),
    position: integer().notNull(),
  },
  (columns) => [primaryKey({ columns: [columns.contentSectionId, columns.bookId] })],
)

export const ContentSectionToBookCollection = pgTable(
  'content_section_to_book_collection',
  {
    contentSectionId: uuid()
      .notNull()
      .references(() => ContentSection.id, { onDelete: 'cascade' }),
    bookCollectionId: uuid()
      .notNull()
      .references(() => BookCollection.id, { onDelete: 'cascade' }),
    position: integer().notNull(),
  },
  (columns) => [primaryKey({ columns: [columns.contentSectionId, columns.bookCollectionId] })],
)

export const ContentSectionToLessonModulePractice = pgTable(
  'content_section_to_lesson_module_practice',
  {
    contentSectionId: uuid()
      .notNull()
      .references(() => ContentSection.id, { onDelete: 'cascade' }),
    lessonModuleId: uuid()
      .notNull()
      .references(() => LessonModule.id, { onDelete: 'cascade' }),
    position: integer().notNull(),
  },
  (columns) => [primaryKey({ columns: [columns.contentSectionId, columns.lessonModuleId] })],
)

export const ContentSectionToLessonModuleInClass = pgTable(
  'content_section_to_lesson_module_in_class',
  {
    contentSectionId: uuid()
      .notNull()
      .references(() => ContentSection.id, { onDelete: 'cascade' }),
    lessonModuleId: uuid()
      .notNull()
      .references(() => LessonModule.id, { onDelete: 'cascade' }),
    position: integer().notNull(),
  },
  (columns) => [primaryKey({ columns: [columns.contentSectionId, columns.lessonModuleId] })],
)

export const InsertContentSectionSchema = createInsertSchema(ContentSection)
export const UpdateContentSectionSchema = createUpdateSchema(ContentSection).omit({ id: true }).partial()
export const SelectContentSectionSchema = createSelectSchema(ContentSection)

export type InsertContentSection = typeof ContentSection.$inferInsert
export type UpdateContentSection = z.infer<typeof UpdateContentSectionSchema>
export type SelectContentSection = typeof ContentSection.$inferSelect

export type InsertContentSectionToBook = typeof ContentSectionToBook.$inferInsert
export type InsertContentSectionToBookCollection = typeof ContentSectionToBookCollection.$inferInsert
export type InsertContentSectionToLessonModulePractice = typeof ContentSectionToLessonModulePractice.$inferInsert
export type InsertContentSectionToLessonModuleInClass = typeof ContentSectionToLessonModuleInClass.$inferInsert
